/**
 * SharedDDPanel Component
 *
 * Main panel for shared DD functionality - shows collaborators,
 * notes, and syndicate summary. Premium feature.
 */

import React, { useState } from 'react';
import {
  View,
  Text,
  Pressable,
  StyleSheet,
  TextInput,
  Modal,
  Keyboard,
} from 'react-native';
import AnimatedPressable from '@/components/AnimatedPressable';
import {
  Users,
  UserPlus,
  Share2,
  X,
  Mail,
  Shield,
  Eye,
  Check,
  ToggleLeft,
  ToggleRight,
} from 'lucide-react-native';
import Animated, { FadeIn, FadeInUp } from 'react-native-reanimated';
import * as Haptics from 'expo-haptics';

import { useThemeColors } from '@/lib/useThemeColors';
import { useKeyboardVisible } from '@/lib/useKeyboardVisible';
import { type CollaboratorRole } from '@/lib/shared-dd';
import {
  useMySharedDDs,
  useInvitedSharedDDs,
  useSharedDD,
  useSharedDDRealtime,
  useCreateSharedDD,
  useInviteCollaborator,
  useRemoveCollaborator,
  useToggleSyndicateMode,
  useAddNote,
  useDeleteNote,
  useReactToNote,
} from '@/lib/shared-dd/use-shared-dd';
import { CollaboratorsList } from './CollaboratorsList';
import { SharedDDNotes, AvailableDocument } from './SharedDDNotes';
import { SyndicateSummary } from './SyndicateSummary';
import { track } from '@/lib/analytics';
import { useAlert } from '@/components/AlertDialog';
import type { AuthUserId } from '@/lib/ids';
import { COPY } from '@/lib/commerce/copy';

// ============================================================================
// Types
// ============================================================================

interface SharedDDPanelProps {
  startupId: string;
  startupName: string;
  /**
   * `auth.users.id` — NOT `investor_profiles.id`.
   *
   * Every table behind this panel is gated on `auth.uid()`: `shared_dd.owner_id`,
   * `dd_notes.author_id`, `dd_note_reactions.investor_user_id`, and
   * `dd_collaborators` via `is_shared_dd_owner()`. None of those columns carries
   * a foreign key, so a profile id here is not rejected by the database — it is
   * rejected by RLS on write (42501) and silently matches zero rows on read.
   * That is exactly what shipped: the prop was called `investorId` and the DD
   * screen passed `investorProfile?.id`, so creating a session failed every time
   * and the lists were permanently empty. See INVERIO-APP-J.
   *
   * Undefined while auth is still loading — writes are guarded on it rather than
   * substituting a placeholder.
   */
  investorUserId: AuthUserId | undefined;
  investorName: string;
  isPremium: boolean;
  /** Optional list of data room documents for linking in notes */
  availableDocuments?: AvailableDocument[];
  /** Called when user clicks on a document reference in a note */
  onDocumentPress?: (documentId: string, documentName: string) => void;
}

// ============================================================================
// Component
// ============================================================================

export function SharedDDPanel({
  startupId,
  startupName,
  investorUserId,
  investorName,
  isPremium,
  availableDocuments = [],
  onDocumentPress,
}: SharedDDPanelProps) {
  const colors = useThemeColors();
  const keyboardVisible = useKeyboardVisible();
  const { alert, error, success, confirmDestructive } = useAlert();
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<CollaboratorRole>('collaborator');
  const [isInviting, setIsInviting] = useState(false);

  // DB-backed data: find this startup's session (owned or joined), then read the
  // authoritative live copy + subscribe to realtime updates.
  const { data: mine = [] } = useMySharedDDs(investorUserId);
  const { data: invited = [] } = useInvitedSharedDDs(investorUserId);
  const sessionStub =
    mine.find((d) => d.startupId === startupId) ??
    invited.find((d) => d.startupId === startupId) ??
    null;
  const { data: liveSharedDD } = useSharedDD(sessionStub?.id);
  useSharedDDRealtime(sessionStub?.id);
  const sharedDD = liveSharedDD ?? sessionStub;

  // Mutations
  const createSharedDD = useCreateSharedDD();
  const inviteCollaborator = useInviteCollaborator();
  const removeCollaborator = useRemoveCollaborator();
  const toggleSyndicateMode = useToggleSyndicateMode();
  const addNote = useAddNote();
  const deleteNote = useDeleteNote();
  const reactToNote = useReactToNote();

  // Auth has not settled yet. EVERY row this panel reads or writes is keyed on
  // this id, so there is nothing meaningful to render and no placeholder that
  // would be correct — substituting one is what put a literal 'investor-123'
  // into a uuid column. Placed after the last hook (so hook order is stable)
  // and before everything else, which narrows the type for the whole body.
  if (!investorUserId) {
    return (
      <View style={styles.loadingState}>
        <Text style={[styles.loadingText, { color: colors.text.tertiary }]}>Loading…</Text>
      </View>
    );
  }

  // `c.investorId` is the DOMAIN field name on SharedDDCollaborator; the value
  // it holds is `dd_collaborators.investor_user_id`, i.e. an auth user id.
  const myRole = sharedDD?.collaborators.find((c) => c.investorId === investorUserId)?.role ?? null;
  const isOwner = sharedDD?.ownerId === investorUserId;

  // Initialize shared DD if premium and doesn't exist
  const handleStartCollaboration = () => {
    if (!isPremium) {
      alert(COPY.sharedDdLocked.alertTitle, COPY.sharedDdLocked.alertBody);
      return;
    }
    Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Medium);
    track('shared_dd_created', { startupId });
    createSharedDD.mutate({ startupId, startupName, ownerId: investorUserId, ownerName: investorName, syndicateMode: false });
  };

  const handleInvite = async () => {
    if (!inviteEmail.trim() || !sharedDD) return;

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(inviteEmail)) {
      error('Invalid Email', 'Please enter a valid email address');
      return;
    }

    setIsInviting(true);
    Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);

    try {
      await inviteCollaborator.mutateAsync({ sharedDdId: sharedDD.id, email: inviteEmail.trim(), role: inviteRole });
      track('shared_dd_invite_sent', { sharedDDId: sharedDD.id, role: inviteRole });
      Haptics.notificationAsync(Haptics.NotificationFeedbackType.Success);
      setInviteEmail('');
      setShowInviteModal(false);
      success('Invite Sent', `Invitation sent to ${inviteEmail}`);
    } catch (e) {
      error('Error', e instanceof Error ? e.message : 'Failed to send invite');
    } finally {
      setIsInviting(false);
    }
  };

  const handleRemoveCollaborator = async (collaboratorId: string) => {
    if (!sharedDD) return;

    if (await confirmDestructive('Remove Collaborator', 'Are you sure?', 'Remove')) {
      Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
      removeCollaborator.mutate({ sharedDdId: sharedDD.id, collaboratorId });
    }
  };

  const handleToggleSyndicate = () => {
    if (!sharedDD) return;
    Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
    toggleSyndicateMode.mutate({ sharedDdId: sharedDD.id, syndicateMode: !sharedDD.syndicateMode });
  };

  // Not yet started collaboration
  if (!sharedDD) {
    return (
      <Animated.View entering={FadeIn.duration(300)}>
        <AnimatedPressable
          onPress={handleStartCollaboration}
          style={[
            styles.startCard,
            {
              backgroundColor: isPremium ? colors.primary[50] : colors.surface.secondary,
              borderColor: isPremium ? colors.primary[200] : colors.border.light,
            },
          ]}
        >
          <View
            style={[
              styles.startIcon,
              { backgroundColor: isPremium ? colors.primary[100] : colors.surface.tertiary },
            ]}
          >
            <Users size={24} color={isPremium ? colors.primary[600] : colors.text.tertiary} />
          </View>
          <Text
            style={[
              styles.startTitle,
              { color: isPremium ? colors.primary[700] : colors.text.secondary },
            ]}
          >
            {isPremium ? 'Start Collaborative DD' : COPY.sharedDdLocked.label}
          </Text>
          <Text style={[styles.startDesc, { color: colors.text.tertiary }]}>
            {isPremium
              ? 'Invite other investors to collaborate on due diligence'
              : COPY.sharedDdLocked.description}
          </Text>
        </AnimatedPressable>
      </Animated.View>
    );
  }

  // Notes are already visibility-filtered server-side by RLS (a collaborator
  // reads public notes + their own private notes).
  const visibleNotes = sharedDD.notes;

  return (
    <View style={styles.container}>
      {/* Header */}
      <View style={styles.header}>
        <View style={styles.headerLeft}>
          <Share2 size={18} color={colors.primary[500]} />
          <Text style={[styles.headerTitle, { color: colors.text.primary }]}>
            Shared DD
          </Text>
        </View>
        {isOwner && (
          <View style={styles.headerActions}>
            <AnimatedPressable
              onPress={handleToggleSyndicate}
              style={[styles.syndicateToggle, { backgroundColor: colors.surface.secondary }]}
            >
              {sharedDD.syndicateMode ? (
                <ToggleRight size={18} color={colors.primary[500]} />
              ) : (
                <ToggleLeft size={18} color={colors.text.tertiary} />
              )}
              <Text
                style={[
                  styles.syndicateText,
                  { color: sharedDD.syndicateMode ? colors.primary[600] : colors.text.tertiary },
                ]}
              >
                Syndicate
              </Text>
            </AnimatedPressable>
            <AnimatedPressable
              onPress={() => setShowInviteModal(true)}
              style={[styles.inviteButton, { backgroundColor: colors.primary[500] }]}
            >
              <UserPlus size={16} color="#fff" />
            </AnimatedPressable>
          </View>
        )}
      </View>

      {/* Syndicate Summary (if enabled) */}
      {sharedDD.syndicateMode && (
        <SyndicateSummary collaborators={sharedDD.collaborators} />
      )}

      {/* Collaborators */}
      <CollaboratorsList
        collaborators={sharedDD.collaborators}
        currentUserId={investorUserId}
        isOwner={isOwner}
        syndicateMode={sharedDD.syndicateMode}
        onRemove={isOwner ? handleRemoveCollaborator : undefined}
      />

      {/* Notes */}
      {myRole && (
        <SharedDDNotes
          notes={visibleNotes}
          currentUserId={investorUserId}
          currentUserName={investorName}
          myRole={myRole}
          availableDocuments={availableDocuments}
          onAddNote={(content, category, isPrivate, documentRef) => {
            addNote.mutate({ sharedDdId: sharedDD.id, authorId: investorUserId, authorName: investorName, content, category, isPrivate, documentRef });
          }}
          onReact={(noteId, type) => {
            reactToNote.mutate({ sharedDdId: sharedDD.id, noteId, investorId: investorUserId, reactionType: type });
          }}
          onDelete={(noteId) => {
            deleteNote.mutate({ sharedDdId: sharedDD.id, noteId });
          }}
          onDocumentPress={onDocumentPress}
        />
      )}

      {/* Invite Modal */}
      <Modal
        visible={showInviteModal}
        transparent
        animationType="fade"
        onRequestClose={() => setShowInviteModal(false)}
      >
        <Pressable
          style={styles.modalOverlay}
          accessibilityLabel={keyboardVisible ? 'Dismiss keyboard' : 'Close'}
          onPress={() => {
            // First tap puts the keyboard away and keeps the half-typed email;
            // only a second tap closes.
            if (keyboardVisible) {
              Keyboard.dismiss();
              return;
            }
            setShowInviteModal(false);
          }}
        >
          <Pressable onPress={(e) => e.stopPropagation()}>
            <Animated.View
              entering={FadeInUp.springify()}
              style={[
                styles.modalContent,
                { backgroundColor: colors.surface.primary },
              ]}
            >
              <View style={styles.modalHeader}>
                <Text style={[styles.modalTitle, { color: colors.text.primary }]}>
                  Invite Collaborator
                </Text>
                <AnimatedPressable onPress={() => setShowInviteModal(false)}>
                  <X size={20} color={colors.text.tertiary} />
                </AnimatedPressable>
              </View>

              {/* Email Input */}
              <View style={styles.inputRow}>
                <View
                  style={[styles.inputIcon, { backgroundColor: colors.surface.secondary }]}
                >
                  <Mail size={18} color={colors.text.tertiary} />
                </View>
                <TextInput
                  style={[
                    styles.input,
                    { backgroundColor: colors.surface.secondary, color: colors.text.primary },
                  ]}
                  placeholder="Email address"
                  placeholderTextColor={colors.text.tertiary}
                  value={inviteEmail}
                  onChangeText={setInviteEmail}
                  keyboardType="email-address"
                  autoCapitalize="none"
                />
              </View>

              {/* Role Selection */}
              <View style={styles.roleSection}>
                <Text style={[styles.roleLabel, { color: colors.text.secondary }]}>
                  Role
                </Text>
                <View style={styles.roleOptions}>
                  <AnimatedPressable
                    onPress={() => setInviteRole('collaborator')}
                    style={[
                      styles.roleOption,
                      {
                        backgroundColor:
                          inviteRole === 'collaborator'
                            ? colors.primary[50]
                            : colors.surface.secondary,
                        borderColor:
                          inviteRole === 'collaborator'
                            ? colors.primary[300]
                            : colors.border.light,
                      },
                    ]}
                  >
                    <Shield size={16} color={colors.primary[500]} />
                    <View style={styles.roleInfo}>
                      <Text style={[styles.roleTitle, { color: colors.text.primary }]}>
                        Collaborator
                      </Text>
                      <Text style={[styles.roleDesc, { color: colors.text.tertiary }]}>
                        Can add notes & conclusions
                      </Text>
                    </View>
                    {inviteRole === 'collaborator' && (
                      <Check size={16} color={colors.primary[500]} />
                    )}
                  </AnimatedPressable>

                  <AnimatedPressable
                    onPress={() => setInviteRole('viewer')}
                    style={[
                      styles.roleOption,
                      {
                        backgroundColor:
                          inviteRole === 'viewer'
                            ? colors.primary[50]
                            : colors.surface.secondary,
                        borderColor:
                          inviteRole === 'viewer'
                            ? colors.primary[300]
                            : colors.border.light,
                      },
                    ]}
                  >
                    <Eye size={16} color={colors.text.tertiary} />
                    <View style={styles.roleInfo}>
                      <Text style={[styles.roleTitle, { color: colors.text.primary }]}>
                        Viewer
                      </Text>
                      <Text style={[styles.roleDesc, { color: colors.text.tertiary }]}>
                        View only, no notes
                      </Text>
                    </View>
                    {inviteRole === 'viewer' && (
                      <Check size={16} color={colors.primary[500]} />
                    )}
                  </AnimatedPressable>
                </View>
              </View>

              {/* Actions */}
              <View style={styles.modalActions}>
                <AnimatedPressable
                  onPress={() => setShowInviteModal(false)}
                  style={[styles.cancelBtn, { backgroundColor: colors.surface.secondary }]}
                >
                  <Text style={[styles.cancelText, { color: colors.text.secondary }]}>
                    Cancel
                  </Text>
                </AnimatedPressable>
                <AnimatedPressable
                  onPress={handleInvite}
                  disabled={isInviting || !inviteEmail.trim()}
                  style={[
                    styles.sendBtn,
                    {
                      backgroundColor: inviteEmail.trim()
                        ? colors.primary[500]
                        : colors.surface.secondary,
                    },
                  ]}
                >
                  <Text
                    style={[
                      styles.sendText,
                      { color: inviteEmail.trim() ? '#fff' : colors.text.tertiary },
                    ]}
                  >
                    {isInviting ? 'Sending...' : 'Send Invite'}
                  </Text>
                </AnimatedPressable>
              </View>
            </Animated.View>
          </Pressable>
        </Pressable>
      </Modal>
    </View>
  );
}

// ============================================================================
// Styles
// ============================================================================

const styles = StyleSheet.create({
  loadingState: {
    paddingVertical: 32,
    alignItems: 'center',
    justifyContent: 'center',
  },
  loadingText: {
    fontSize: 14,
  },
  container: {
    gap: 16,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  headerLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  headerTitle: {
    fontSize: 16,
    fontWeight: '600',
  },
  headerActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  syndicateToggle: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 8,
  },
  syndicateText: {
    fontSize: 12,
    fontWeight: '500',
  },
  inviteButton: {
    width: 36,
    height: 36,
    borderRadius: 10,
    alignItems: 'center',
    justifyContent: 'center',
  },
  startCard: {
    alignItems: 'center',
    padding: 24,
    borderRadius: 16,
    borderWidth: 1,
    gap: 10,
  },
  startIcon: {
    width: 56,
    height: 56,
    borderRadius: 28,
    alignItems: 'center',
    justifyContent: 'center',
  },
  startTitle: {
    fontSize: 15,
    fontWeight: '600',
  },
  startDesc: {
    fontSize: 13,
    textAlign: 'center',
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  modalContent: {
    width: '100%',
    maxWidth: 400,
    borderRadius: 20,
    padding: 20,
    gap: 16,
  },
  modalHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: '600',
  },
  inputRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  inputIcon: {
    width: 44,
    height: 44,
    borderRadius: 10,
    alignItems: 'center',
    justifyContent: 'center',
  },
  input: {
    flex: 1,
    height: 44,
    borderRadius: 10,
    paddingHorizontal: 14,
    fontSize: 15,
  },
  roleSection: {
    gap: 8,
  },
  roleLabel: {
    fontSize: 13,
    fontWeight: '500',
  },
  roleOptions: {
    gap: 8,
  },
  roleOption: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    padding: 12,
    borderRadius: 12,
    borderWidth: 1,
  },
  roleInfo: {
    flex: 1,
  },
  roleTitle: {
    fontSize: 14,
    fontWeight: '500',
  },
  roleDesc: {
    fontSize: 12,
  },
  modalActions: {
    flexDirection: 'row',
    gap: 10,
  },
  cancelBtn: {
    flex: 1,
    alignItems: 'center',
    paddingVertical: 12,
    borderRadius: 10,
  },
  cancelText: {
    fontSize: 14,
    fontWeight: '600',
  },
  sendBtn: {
    flex: 1,
    alignItems: 'center',
    paddingVertical: 12,
    borderRadius: 10,
  },
  sendText: {
    fontSize: 14,
    fontWeight: '600',
  },
});
